import React from "react";
import { Theme } from "../../../providers/ThemeProvider";
import { getPanelQuestions, hasUnansweredQuestions } from "./labPanelQuestions";
import {
  formatDisplayDate,
  formatDisplayTime,
} from "../../../common/utils/date";

/**
 * Lab-panel / order status resolution.
 *
 * The backend now sends a single status enum per customer lab panel
 * (`enCustomerPanelStatus`, exposed on the panel/order as `panelStatus`), which
 * maps 1:1 to the badge shown in the UI. The previous composite derivation from
 * four separate fields (status / paymentStatus / labStatus / approvalStatus) is
 * gone — the displayed status is now a direct lookup on this single value.
 *
 * NOTE: partial approvals are folded into `ApprovalCompleted` by the backend
 * (there is no separate "partially approved" value).
 */

// Single source of truth from the backend — panel.panelStatus.
export enum EnCustomerPanelStatus {
  Pending = 1, // Authorize payment
  ProcessingPayment = 2, // submitted to payment processor
  PaymentFailed = 3, // rejected by payment processor
  AwaitingDetails = 4, // failed Junction validation / couldn't submit — needs details
  OrderReceived = 5, // payment completed, submitted to Junction
  SampleTaken = 6, // Junction in progress
  LabAnalysis = 7, // Junction in progress
  ApprovalNeeded = 8, // results received & processed
  ApprovalCompleted = 9, // approved
  ApprovalRejected = 10, // rejected
  OrderFailed = 11, // rejected by Junction
  OrderCanceled = 12, // currently not used
  // The lab (Junction) sample-collection appointment events (SD-6758). Both
  // also write the panel's `appointmentDate`: the cancel nulls it, the set
  // stores the date/time (first booking and any re-booking alike). Neither
  // has an action.
  AppointmentCancelled = 13,
  AppointmentSet = 14,
}

export type StatusTone =
  | "info"
  | "warning"
  | "success"
  | "danger"
  | "neutral";

interface BaseStatus {
  // stable identifier, e.g. for filter option keys
  key: string;
  // human-facing badge label
  label: string;
  tone: StatusTone;
  // true when the affiliate must act (approve / authorize / resolve an error).
  // Drives the colored left-row accent and the hover "Review" tile.
  actionRequired: boolean;
}

export interface ResolvedStatus extends BaseStatus {
  // Descriptive sentence for the status, rendered as a banner in the order
  // detail (and, later, the prescription detail). Sourced from the resolver so
  // every consumer shows the same copy.
  message: string;
  // When set, overrides the static STATUS_ACTIONS for this status. Used by the
  // failure statuses, whose action depends on the rejection reason.
  actions?: StatusActionKind[];
}

/**
 * Per-backend-status display metadata (badge label, tone, action requirement).
 * Keys are stable identifiers reused by STATUS_MESSAGES / STATUS_ACTIONS below.
 */
const STATUS_BY_ENUM: Record<EnCustomerPanelStatus, BaseStatus> = {
  [EnCustomerPanelStatus.Pending]: {
    key: "authorize_payment",
    label: "Authorize payment",
    tone: "warning",
    actionRequired: true,
  },
  [EnCustomerPanelStatus.ProcessingPayment]: {
    key: "processing_payment",
    label: "Processing Payment",
    tone: "info",
    actionRequired: false,
  },
  [EnCustomerPanelStatus.PaymentFailed]: {
    key: "payment_failed",
    label: "Payment Failed",
    tone: "danger",
    actionRequired: true,
  },
  [EnCustomerPanelStatus.AwaitingDetails]: {
    key: "awaiting_details",
    label: "Awaiting details",
    tone: "warning",
    actionRequired: true,
  },
  [EnCustomerPanelStatus.OrderReceived]: {
    key: "order_received",
    label: "Order Received",
    tone: "info",
    actionRequired: false,
  },
  [EnCustomerPanelStatus.SampleTaken]: {
    key: "sample_taken",
    label: "Sample taken",
    tone: "info",
    actionRequired: false,
  },
  [EnCustomerPanelStatus.LabAnalysis]: {
    key: "lab_analysis",
    label: "Lab Analysis",
    tone: "info",
    actionRequired: false,
  },
  [EnCustomerPanelStatus.ApprovalNeeded]: {
    key: "approval_needed",
    label: "Approval needed",
    tone: "warning",
    actionRequired: true,
  },
  [EnCustomerPanelStatus.ApprovalCompleted]: {
    key: "completed_approved",
    label: "Completed - Approved",
    tone: "success",
    actionRequired: false,
  },
  [EnCustomerPanelStatus.ApprovalRejected]: {
    key: "completed_rejected",
    label: "Completed - Rejected",
    tone: "danger",
    actionRequired: true,
  },
  [EnCustomerPanelStatus.OrderFailed]: {
    key: "order_failed",
    label: "Order Failed",
    tone: "danger",
    actionRequired: true,
  },
  [EnCustomerPanelStatus.OrderCanceled]: {
    key: "order_canceled",
    label: "Order Canceled",
    tone: "danger",
    actionRequired: true,
  },
  [EnCustomerPanelStatus.AppointmentCancelled]: {
    key: "appointment_canceled",
    label: "Appointment cancelled",
    tone: "warning",
    actionRequired: false,
  },
  [EnCustomerPanelStatus.AppointmentSet]: {
    key: "appointment_set",
    label: "Appointment scheduled",
    tone: "info",
    actionRequired: false,
  },
};

/**
 * Per-status message copy (keyed by the resolver's stable `key`).
 * Wording follows the Lab Ordering BRD §5.4 status definitions; the
 * "approval_needed" copy is the verbatim string from the Figma design.
 */
export const STATUS_MESSAGES: Record<string, string> = {
  authorize_payment:
    "The order has been received. Please authorize the payment in order to proceed.",
  processing_payment:
    "The payment authorization is being processed by the bank. This usually takes a few moments.",
  payment_failed:
    "Payment failed due to an issue with your saved Stripe payment information. Visit https://platform.mypnoe.com to update the payment details and try again.",
  awaiting_details:
    "The payment was completed, but the laboratory couldn't accept the order because some of the client's details are invalid. Please contact support or your metabolic expert for assistance.",
  order_received:
    "The laboratory has officially received your order and is verifying the requirements. Analysis will begin shortly.",
  sample_taken:
    "The biological sample has been successfully collected and is being prepared for transport to the laboratory.",
  lab_analysis:
    "The lab has received the samples and is currently performing the analysis.",
  approval_needed:
    "Results are ready for your review. Please approve to proceed.",
  completed_approved:
    "The results are approved and have been integrated into the biomarkers tab.",
  completed_rejected:
    'The results were rejected and have not been integrated into the biomarkers tab. To revert this action click "Approve".',
  order_failed:
    "The lab failed to process your order. For more information contact support or your metabolic expert.",
  order_canceled: "This order has been cancelled and will not be processed.",
  // Appointment copy per ticket SD-6758. `appointment_set` is completed with
  // the panel's `appointmentDate` ("… for {date} at {time}.") when the backend
  // sends one; without it the sentence stands alone.
  appointment_canceled:
    "The sample collection appointment has been cancelled. A new appointment needs to be scheduled to continue with this order.",
  appointment_set: "A sample collection appointment has been scheduled",
  awaiting_question_answers:
    "The lab requires answers to this panel's order-entry questions before the order can be placed. Please answer them to proceed with the payment.",
};

/**
 * A panel that carries ask-at-order-entry (AOE) questions. The flag is
 * `is_aoe`: on a lab-panel template it sits on the panel; on a customer panel
 * (prescription / order) it sits on the item that has the questions, so both
 * places are read. A panel that carries questions is AOE whatever the flag.
 */
export const isAoePanel = (panel: any): boolean => {
  if (!panel) return false;
  if (panel.is_aoe === true) return true;
  const items: any[] = Array.isArray(panel.items) ? panel.items : [];
  if (items.some((item) => item?.is_aoe === true)) return true;
  return getPanelQuestions(panel).length > 0;
};

/**
 * The panel is waiting for the answers to the lab's order-entry questions
 * before it can be ordered, so it must not be charged yet: the lab would
 * refuse the order. Two sources say so:
 * - the backend itself: `panelStatus = AwaitingDetails` on an AOE panel (the
 *   authoritative signal; a non-AOE panel on that status is one the lab
 *   rejected after payment — see AWAITING_DETAILS_REASONS);
 * - a `Pending` panel whose required questions are still unanswered (the
 *   older shape, where the backend reported such panels as Pending).
 */
export const isAwaitingQuestionAnswers = (panel: any): boolean => {
  const status = num(panel?.panelStatus);
  if (status === EnCustomerPanelStatus.AwaitingDetails)
    return isAoePanel(panel);
  return (
    status === EnCustomerPanelStatus.Pending && hasUnansweredQuestions(panel)
  );
};

/**
 * Shown when a finalize/charge is attempted but no payment method is on file.
 * Worded like `payment_failed` above, but for the missing-method case (surfaced
 * client-side via the snackbar before the FinalizeCustomerLabPanel call).
 */
export const NO_PAYMENT_METHOD_MESSAGE =
  "No saved payment method was found. Visit https://platform.mypnoe.com to add your payment details and try again.";

/**
 * Friendly copy for known Stripe failure texts the backend passes through
 * verbatim on a finalize/charge error. The frontend can't know which Stripe
 * mode (live/test) the backend key or the client's Stripe customer belongs
 * to, so the only reliable signal is Stripe's own wording — which is stable
 * for these cases. Order matters: the environment-mismatch text also contains
 * "No such customer", so it must be matched first.
 */
export const PAYMENT_FAILURE_MESSAGES = {
  environmentMismatch:
    "This client's payment profile isn't available in this environment, so the payment can't be processed from here. Please authorize this order from the production platform, or contact support.",
  customerNotFound:
    "No payment profile was found for this account. Visit https://platform.mypnoe.com to add your payment details and try again.",
  cardDeclined:
    "The payment was declined by the bank. Visit https://platform.mypnoe.com to check the saved payment details and try again.",
  cardExpired:
    "The saved card has expired. Visit https://platform.mypnoe.com to update the payment details and try again.",
  authenticationRequired:
    "The bank requires additional authentication for this card. Visit https://platform.mypnoe.com to confirm the payment method and try again.",
} as const;

const PAYMENT_FAILURE_PATTERNS: Array<{
  pattern: RegExp;
  message: string;
}> = [
  {
    // "No such customer: 'cus_…'; a similar object exists in live mode, but a
    // test mode key was used to make this request." (and the reverse).
    pattern: /similar object exists in (live|test) mode/i,
    message: PAYMENT_FAILURE_MESSAGES.environmentMismatch,
  },
  {
    pattern: /no such customer/i,
    message: PAYMENT_FAILURE_MESSAGES.customerNotFound,
  },
  {
    pattern: /expired[_ ]card|card has expired/i,
    message: PAYMENT_FAILURE_MESSAGES.cardExpired,
  },
  {
    pattern: /authentication[_ ]required/i,
    message: PAYMENT_FAILURE_MESSAGES.authenticationRequired,
  },
  {
    pattern: /card was declined|card_declined|insufficient[_ ]funds/i,
    message: PAYMENT_FAILURE_MESSAGES.cardDeclined,
  },
];

/**
 * Turn the backend's raw finalize error text into the snackbar message. Known
 * Stripe phrases map to friendly copy; anything else is quoted as before so
 * the affiliate still sees the reason. Empty text → the generic fallback.
 */
export const getPaymentFailureMessage = (backendMessage: string): string => {
  const text = (backendMessage ?? "").trim();
  if (!text) {
    return "We couldn't complete the payment. Please check that a payment method exists on file and try again.";
  }
  const match = PAYMENT_FAILURE_PATTERNS.find(({ pattern }) =>
    pattern.test(text)
  );
  if (match) return match.message;
  return `We couldn't complete the payment because we received the following message: "${text}"`;
};

/**
 * Actions offered per status (rendered as inline banner buttons).
 * - authorize / retry → the finalize (authorize payment) flow
 * - approve / reject   → results approval (wired later; console.log for now)
 * The mapping of kind → label/handler lives in the consuming component.
 */
export type StatusActionKind =
  | "authorize"
  | "retry"
  | "approve"
  | "reject"
  | "update_details"
  // awaiting_details (questions pending) → open the panel editor to answer
  // the lab's order-entry questions before the payment is authorized.
  | "answer_questions"
  // order_failed → re-submit the already-paid order to the lab
  // (RetryLabPanel); distinct from `retry`, which re-attempts the charge.
  | "retry_order";

export const STATUS_ACTIONS: Record<string, StatusActionKind[]> = {
  authorize_payment: ["authorize"],
  payment_failed: ["retry"],
  approval_needed: ["reject", "approve"],
  completed_rejected: ["approve"],
  order_failed: ["retry_order"],
};

const num = (v: any): number => (v === undefined || v === null ? 0 : Number(v));

/**
 * The backend's free-text explanation of the panel's current status. It is
 * populated on every failure path — a declined/rejected transaction, a failed
 * Junction validation, a lab rejection — e.g.
 *   "Transaction failed (This value must be greater than or equal to 1.)"
 * The field is `statusLog`; the others are kept only as fallbacks in case an
 * endpoint returns one of the older shapes.
 */
export const getPanelStatusLog = (panel: any): string =>
  (
    panel?.statusLog ??
    panel?.errorMessage ??
    panel?.statusMessage ??
    panel?.error ??
    ""
  )
    .toString()
    .trim();

/**
 * The lab's own reason from a structured `statusLog`. Lab-provider rejections
 * arrive as JSON, e.g.
 *   {"http_error_code":{…,"status":400},"message":"bad request from upstream
 *    lab provider","data":{"detail":"Lab Quest does not support state NY"}}
 * where only `data.detail` means anything to an affiliate. Empty when the log
 * is plain text (classified by `matchStatusReason` instead) or has no detail.
 */
export const getPanelStatusDetail = (panel: any): string => {
  const log = getPanelStatusLog(panel);
  if (!log.startsWith("{")) return "";
  try {
    const detail = JSON.parse(log)?.data?.detail;
    return typeof detail === "string" ? detail.trim() : "";
  } catch {
    return "";
  }
};

/**
 * The reason to show for a failed order: the structured detail when the log
 * is JSON, the log itself when it is plain text, never the raw JSON.
 */
export const getPanelFailureReason = (panel: any): string => {
  const log = getPanelStatusLog(panel);
  return log.startsWith("{") ? getPanelStatusDetail(panel) : log;
};

/**
 * Several statuses cover more than one root cause, and the copy + offered
 * action differ per cause (a payment worth retrying vs. one that can't succeed
 * as the order stands). We classify by matching the panel's `statusLog` against
 * known reasons, with a fallback for anything unrecognized.
 */
interface StatusReason {
  // Case-insensitive substrings; the first reason with any hit wins.
  match: string[];
  message: string;
  // Overrides STATUS_ACTIONS for this status when set (`[]` = no action).
  actions?: StatusActionKind[];
}

interface StatusReasons {
  list: StatusReason[];
  fallback: Omit<StatusReason, "match">;
}

const AWAITING_DETAILS_REASONS: StatusReasons = {
  list: [
    {
      match: ["Invalid phone number"],
      message:
        "The payment was completed, but the laboratory couldn't accept the order because the client's phone number is invalid. Please update the client's details and retry.",
      actions: ["update_details"],
    },
    {
      match: [
        "Invalid zip code",
        "Invalid State code",
        "Invalid City",
        "Invalid Country code",
        "Invalid Address",
      ],
      message:
        "The payment was completed, but the laboratory couldn't accept the order because the client's address is invalid. Please update the client's details and retry.",
      actions: ["update_details"],
    },
  ],
  // Unknown rejection reason → no "Update details" button, since we can't say
  // which detail to fix.
  fallback: { message: STATUS_MESSAGES.awaiting_details, actions: [] },
};

const PAYMENT_FAILED_REASONS: StatusReasons = {
  list: [
    {
      // The payment provider refuses the charge before it ever reaches the card
      // because the amount itself is invalid — a panel whose total sits below
      // (or rounds to under) the smallest chargeable amount, e.g. total 0.4 →
      // "Transaction failed (This value must be greater than or equal to 1.)".
      // A retry just replays the same rejection, so we offer none: the panel's
      // pricing has to change first.
      match: [
        "greater than or equal to 1",
        "amount_too_small",
        "amount too small",
        "invalid amount",
        "amount must be",
      ],
      message:
        "The payment couldn't be processed because the order's total amount is not chargeable by the payment provider.",
      actions: [],
    },
  ],
  // Anything else (declined card, missing/expired method, gateway error) is
  // worth retrying once the payment details are fixed.
  fallback: { message: STATUS_MESSAGES.payment_failed },
};

const STATUS_REASONS: Record<string, StatusReasons> = {
  awaiting_details: AWAITING_DETAILS_REASONS,
  payment_failed: PAYMENT_FAILED_REASONS,
};

const matchStatusReason = (
  reasons: StatusReasons,
  statusLog: string
): Omit<StatusReason, "match"> => {
  const text = statusLog.toLowerCase();
  return (
    reasons.list.find((reason) =>
      reason.match.some((m) => text.includes(m.toLowerCase()))
    ) ?? reasons.fallback
  );
};

/**
 * Statuses a finalize (authorize/retry) attempt can come back with that mean
 * the order is NOT live: the charge was refused (PaymentFailed), or it went
 * through but the laboratory refused the order (AwaitingDetails / OrderFailed /
 * OrderCanceled). A finalize returning any of these is a 200 that must be
 * reported as a failure, not as "payment completed".
 */
const FINALIZE_FAILURE_STATUSES: EnCustomerPanelStatus[] = [
  EnCustomerPanelStatus.PaymentFailed,
  EnCustomerPanelStatus.AwaitingDetails,
  EnCustomerPanelStatus.OrderFailed,
  EnCustomerPanelStatus.OrderCanceled,
];

export const isFinalizeFailureStatus = (panel: any): boolean =>
  FINALIZE_FAILURE_STATUSES.includes(
    num(panel?.panelStatus) as EnCustomerPanelStatus
  );

/**
 * Public resolver: the display status (plus its message copy) for a panel/order,
 * looked up directly from the backend's single `panelStatus` enum value.
 * Single source of truth for the badge, row treatment, and status message.
 */
export const resolveLabPanelStatus = (panel: any): ResolvedStatus | null => {
  const resolved = resolveStatusCopy(panel);
  // A failed order whose log carries the lab's reason names it, so the
  // affiliate knows what to fix (e.g. a state the lab doesn't serve).
  const detail = resolved && isFinalizeFailureStatus(panel)
    ? getPanelStatusDetail(panel)
    : "";
  if (!resolved || !detail) return resolved;
  const reason = /[.!?]$/.test(detail) ? detail : `${detail}.`;
  // One sentence: what happened, then the lab's reason after a colon; any
  // follow-up ("For more information contact…") stays after it.
  const [, lead, rest] =
    resolved.message.match(/^(.*?)[.!?](?:\s+(.*))?$/s) ?? [];
  const message = lead
    ? [`${lead}: ${reason}`, rest].filter(Boolean).join(" ")
    : `${resolved.message} ${reason}`.trim();
  return { ...resolved, message };
};

const resolveStatusCopy = (panel: any): ResolvedStatus | null => {
  if (!panel) return null;
  const base = STATUS_BY_ENUM[num(panel.panelStatus) as EnCustomerPanelStatus];
  if (!base) return null;

  // A panel whose order-entry questions still have to be answered cannot be
  // ordered yet: the lab refuses the order without the answers, so charging
  // first would only take the money for nothing. It reads "Awaiting details"
  // with the action that collects the answers; once they are saved the panel
  // reads "Authorize payment". A non-AOE panel on AwaitingDetails keeps the
  // reason-based copy below, with no answer action.
  if (isAwaitingQuestionAnswers(panel)) {
    return {
      ...STATUS_BY_ENUM[EnCustomerPanelStatus.AwaitingDetails],
      message: STATUS_MESSAGES.awaiting_question_answers,
      actions: ["answer_questions"],
    };
  }

  const statusLog = getPanelStatusLog(panel);
  const reasons = STATUS_REASONS[base.key];

  // Statuses with more than one root cause pick their copy (and whether an
  // action is worth offering) from the backend's statusLog; the rest use the
  // single static message. `actions: undefined` leaves STATUS_ACTIONS in charge.
  if (reasons) {
    const { message, actions } = matchStatusReason(reasons, statusLog);
    return { ...base, message, actions };
  }

  // AppointmentSet carries the booking itself: the backend writes the panel's
  // `appointmentDate` on that event, so the message names the date/time when
  // it is there (and stays generic when it is not).
  if (base.key === "appointment_set") {
    const date = formatDisplayDate(panel.appointmentDate);
    const time = formatDisplayTime(panel.appointmentDate);
    return {
      ...base,
      message: date
        ? `${STATUS_MESSAGES.appointment_set} for ${date} at ${time}.`
        : `${STATUS_MESSAGES.appointment_set}.`,
    };
  }

  return { ...base, message: STATUS_MESSAGES[base.key] ?? "" };
};

/**
 * ─── Prescription save → automatic lab-panel payment ────────────────────────
 *
 * Saving a prescription that contains lab panels (CustomerPrescription/Update)
 * makes the backend charge each panel right away. The call still returns 200
 * when a charge is refused — the saved prescription comes back with the panel
 * on a failure status and the reason in `statusLog`, e.g.
 *   { "statusLog": "Transaction failed (Your card was declined.)",
 *     "paymentTransactionID": null, ... }
 * so the save flow has to read the response, not just its HTTP status, to tell
 * the affiliate the prescription was saved but the payment was not.
 */
export interface PrescriptionSaveFailure {
  title: string;
  message: string;
}

const isPanelPaymentFailed = (panel: any): boolean => {
  if (!panel) return false;
  // Prefer the status enum when the response carries it; fall back to the
  // statusLog + missing transaction pair for responses that don't.
  if (panel.panelStatus !== undefined && panel.panelStatus !== null) {
    return isFinalizeFailureStatus(panel);
  }
  return !!getPanelStatusLog(panel) && !panel.paymentTransactionID;
};

/** The lab panels of a CustomerPrescription/Update response, whichever shape it has. */
export const getSavedPrescriptionPanels = (saved: any): any[] => {
  if (Array.isArray(saved?.labPanels)) return saved.labPanels;
  if (Array.isArray(saved?.data?.labPanels)) return saved.data.labPanels;
  return [];
};

/**
 * Inspect the CustomerPrescription/Update response for lab panels whose
 * automatic payment (or lab submission) failed. Returns the alert-snackbar copy
 * when at least one did, `null` when every panel went through (or there were
 * none). The reason reuses the same resolution as the Retry flow in the
 * prescription detail: the status-specific copy for charged-but-refused
 * statuses, and the Stripe-aware `getPaymentFailureMessage` for a declined
 * charge, so both surfaces explain the failure the same way.
 */
export const getPrescriptionSaveFailure = (
  saved: any
): PrescriptionSaveFailure | null => {
  const failed = getSavedPrescriptionPanels(saved).filter(isPanelPaymentFailed);
  if (failed.length === 0) return null;

  const [first] = failed;
  const resolved = resolveLabPanelStatus(first);
  const isPaymentFailure = !resolved || resolved.key === "payment_failed";

  let reason: string;
  if (!isPaymentFailure) {
    reason = resolved!.message;
  } else if (resolved && resolved.message !== STATUS_MESSAGES.payment_failed) {
    // A reason-specific payment_failed copy (e.g. amount not chargeable).
    reason = resolved.message;
  } else {
    reason = getPaymentFailureMessage(getPanelStatusLog(first));
  }

  const names = failed
    .map((panel) => (panel?.name ?? "").toString().trim())
    .filter(Boolean);
  const subject =
    failed.length === 1
      ? `Lab panel${names[0] ? ` "${names[0]}"` : ""}`
      : `${failed.length} lab panels`;

  // The title already says "saved, but failed", so the body stays to one short
  // line per fact: which panel, the first sentence of the reason (the shared
  // copy goes on to explain where to fix the payment method, which the detail
  // view's banner repeats in full), and where the retry lives.
  const shortReason = reason.split(/(?<=\.)\s+/)[0];

  return {
    title: isPaymentFailure
      ? "Prescription saved, but the payment failed"
      : "Prescription saved, but the lab order failed",
    message: `${subject}: ${shortReason}\nRetry from the prescription details.`,
  };
};

/**
 * A lab panel can be edited / removed from a prescription only before payment is
 * captured — i.e. while awaiting authorization or after a failed payment. Any
 * later status (order received, analysis, completed, …) locks it; the backend
 * keeps such panels when the rest of the prescription is deleted.
 */
export const isLabPanelEditable = (panel: any): boolean => {
  // On the backend's own status, not the displayed one: a pending panel shown
  // as "Awaiting details" over its unanswered questions is exactly the one
  // that has to be edited. So is an AOE panel the backend itself reports as
  // AwaitingDetails — its answers are given in the same editor, before any
  // payment. A non-AOE panel on that status was already charged, so it stays
  // locked.
  const status = num(panel?.panelStatus) as EnCustomerPanelStatus;
  return (
    status === EnCustomerPanelStatus.Pending ||
    status === EnCustomerPanelStatus.PaymentFailed ||
    (status === EnCustomerPanelStatus.AwaitingDetails && isAoePanel(panel))
  );
};

/**
 * True once the lab has returned results, i.e. the panel is at approval or
 * completed. Drives whether the biomarker list is shown as the results table
 * (values/ranges/dates) instead of the plain ordered-biomarker name list.
 */
export const labPanelHasResults = (panel: any): boolean => {
  const key = resolveLabPanelStatus(panel)?.key;
  return (
    key === "approval_needed" ||
    key === "completed_approved" ||
    key === "completed_rejected"
  );
};

/**
 * ─── A panel's status AT THE LAB ────────────────────────────────────────────
 *
 * `lab_panel_status` on a *user* lab panel (the affiliate's own custom panel)
 * is the lab's own lifecycle for that panel definition, and is unrelated to
 * `panelStatus` above, which tracks ONE customer's order through payment,
 * analysis and approval. The lab returns a string; observed values are
 * "active" and "pending_approval", plus "inactive" for a panel the lab has
 * retired. A `null` means the panel was never submitted (it has no
 * `lab_panel_id`), i.e. it only exists on our side.
 *
 * Provider catalog panels ("{Provider} default") carry no such field at all,
 * so this only applies to the custom listing.
 */
const PANEL_LAB_STATUS_INACTIVE = "inactive";

const panelLabStatus = (panel: any): string =>
  (panel?.lab_panel_status ?? "").toString().trim().toLowerCase();

/**
 * Retired-at-the-lab panels are hidden from the custom listing — they can no
 * longer be ordered, so an affiliate has nothing to do with them.
 */
export const isPanelInactiveAtLab = (panel: any): boolean =>
  panelLabStatus(panel) === PANEL_LAB_STATUS_INACTIVE;

/**
 * Only a panel the lab has activated can actually be ordered, so only those may
 * be picked in the Select Lab Panel modal — pending, retired and never-submitted
 * panels are shown but not selectable. Provider catalog panels carry no status
 * and are always selectable.
 */
export const isPanelOrderable = (panel: any): boolean => {
  if (!panel || !("lab_panel_status" in panel)) return true;
  return panelLabStatus(panel) === "active";
};

/**
 * Badge (label + tone) for a custom panel's lab status. Returns null only for
 * the inactive case, whose rows are filtered out before rendering.
 */
export const resolvePanelLabStatus = (
  panel: any
): { label: string; tone: StatusTone } | null => {
  // Provider catalog panels don't carry the field at all, so they get no badge
  // — distinct from a custom panel whose value is null ("Not submitted").
  if (!panel || !("lab_panel_status" in panel)) return null;
  const status = panelLabStatus(panel);
  // Never sent to the lab — a panel that exists only on our side.
  if (!status) return { label: "Not submitted", tone: "neutral" };
  switch (status) {
    case "active":
      return { label: "Active", tone: "success" };
    case "pending_approval":
    case "pending":
      return { label: "Pending", tone: "warning" };
    case PANEL_LAB_STATUS_INACTIVE:
      return null;
    default:
      // An unrecognized value is still worth showing (sentence-cased) rather
      // than leaving the cell blank.
      return {
        label:
          status.replace(/_/g, " ").charAt(0).toUpperCase() +
          status.replace(/_/g, " ").slice(1),
        tone: "info",
      };
  }
};

const toneColors = (
  tone: StatusTone,
  theme: Theme
): { bg: string; border: string; text: string } => {
  switch (tone) {
    case "neutral":
      return {
        bg: theme.colorTokens.N[30],
        border: theme.colorTokens.N[60],
        text: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
      };
    case "warning":
      return {
        bg: theme.colorTokens.W[10],
        border: theme.colorTokens.W[70],
        text: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
      };
    case "success":
      return {
        bg: theme.colorTokens.S[10],
        border: theme.colorTokens.S[70],
        text: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
      };
    case "danger":
      return {
        bg: theme.colorTokens.D[10],
        border: theme.colorTokens.D[70],
        text: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
      };
    case "info":
    default:
      return {
        bg: theme.colorTokens.P[10],
        border: theme.colorTokens.P[70],
        text: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
      };
  }
};

/**
 * The three colors a tone is made of, for callers that need the palette but
 * not `getStatusBadgeStyle`'s exact metrics (the PNOĒ status select's trigger
 * pill is the same colors at a larger size).
 */
export const getStatusToneColors = (
  tone: StatusTone,
  theme: Theme
): { bg: string; border: string; text: string } => toneColors(tone, theme);

/** Badge container style for a resolved status tone. */
export const getStatusBadgeStyle = (
  tone: StatusTone,
  theme: Theme
): React.CSSProperties => {
  const c = toneColors(tone, theme);
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    padding: "4px 8px",
    borderRadius: "8px",
    backgroundColor: c.bg,
    border: `1px solid ${c.border}`,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.6",
    color: c.text,
    whiteSpace: "nowrap",
  };
};

/** Icon color for the ⓘ mark shown on action-required (warning/danger) badges. */
export const getStatusIconColor = (tone: StatusTone, theme: Theme): string =>
  toneColors(tone, theme).border;

/** The colored left-row accent shown on rows needing attention. */
export const getRowAccentColor = (
  resolved: ResolvedStatus | null,
  theme: Theme
): string | null => {
  if (!resolved || !resolved.actionRequired) return null;
  return resolved.tone === "danger"
    ? theme.colorTokens.D[70]
    : theme.colorTokens.W[70];
};

/** Tinted background + icon color for the status-message banner, per tone. */
export const getStatusMessageColors = (
  tone: StatusTone,
  theme: Theme
): { backgroundColor: string; iconColor: string } => {
  const c = toneColors(tone, theme);
  return {
    backgroundColor: tone === "info" ? theme.colorTokens.P[10] : c.bg,
    iconColor: c.border,
  };
};
